/**
 * Argus Tool Developer — AI-Assisted Tool Creation
 *
 * Split layout:
 *   Left: Conversation with Argus (AI-powered tool builder)
 *   Right: Monaco code editor with live code sync
 *
 * Features:
 * - Conversation-driven tool development
 * - File upload for reference docs, code snippets, API specs
 * - AI generates code, reviews it, suggests improvements
 * - Bidirectional sync: AI writes to editor, user edits visible to AI
 * - Code review with line-specific feedback
 * - Test case generation and execution
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Send, Paperclip, X, FileText, FileCode,
  Sparkles, CheckCircle, XCircle, AlertTriangle, Info,
  Copy, Maximize2, Minimize2, AlignLeft, Play, Rocket,
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from '@tanstack/react-router';
import { ArgusEye } from '@/components/icons';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import {
  getWelcomeMessages,
  handleFileUpload as processFileUpload,
} from './conversation-engine';
import type { ToolDevMessage, SuggestionChip, FileAttachment, ReviewItem, ToolDevPhase } from './types';
// Day 5 §6.4 — wire the real Function Helper / Tool Helper LLM via
// `useHelperChat`. This replaced the dead-code `generateMockResponse`
// keyword-matcher + setTimeout pattern that always returned a
// hardcoded `custom.equipment_status` plant-API tool regardless of
// the user's actual request (bug #95). The mock branches +
// `MOCK_TS_CODE` / `MOCK_TS_IMPROVED` / `MOCK_PY_CODE` templates
// were also removed from `conversation-engine.ts` so they can't be
// resurrected by a future import.
import { useHelperChat, type HelperScope, type PendingMutation } from '@/lib/use-studio-helper';
import { AcceptRejectCard } from '@/components/canvas/accept-reject-card';

// ── Helpers ────────────────────────────────────────────────────────────────

let _idCounter = 0;
function makeId() { return `tdm_${++_idCounter}_${Date.now()}`; }

function makeMessage(partial: Omit<ToolDevMessage, 'id' | 'timestamp'>): ToolDevMessage {
  return { ...partial, id: makeId(), timestamp: new Date() };
}

const PHASE_LABELS: Record<ToolDevPhase, { label: string; color: string }> = {
  discover: { label: 'Discovering', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  clarify: { label: 'Clarifying', color: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  generate: { label: 'Generating', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  review: { label: 'Reviewing', color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400' },
  refine: { label: 'Refining', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  test: { label: 'Testing', color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  ready: { label: 'Ready', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
};

const REVIEW_ICONS: Record<string, { icon: typeof CheckCircle; color: string }> = {
  success: { icon: CheckCircle, color: 'text-emerald-600 dark:text-emerald-400' },
  warning: { icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400' },
  error: { icon: XCircle, color: 'text-red-600 dark:text-red-400' },
  info: { icon: Info, color: 'text-blue-600 dark:text-blue-400' },
};

// ── Main Component ─────────────────────────────────────────────────────────

interface ToolDeveloperPageProps {
  /**
   * Called when user saves the function. Receives current draft state.
   *
   * The optional fourth `language` arg (`'typescript' | 'python'`) was
   * added 2026-05-12 for the sysadmin functions-expansion route, which
   * needs to map the chat-driven language back into the wrapper-function
   * backend's `'ts' | 'py'` enum. Existing tenant callers are
   * untouched — they ignore the extra positional arg.
   */
  onSave?: (
    code: string,
    name: string,
    description: string,
    language?: 'typescript' | 'python',
    definition?: {
      inputSchemaJson?: string;
      outputSchemaJson?: string;
      sideEffects?: string[];
      networkHosts?: string[];
      tests?: Array<{ name: string; inputJson: string; expectedJson?: string }>;
    },
  ) => void;
  /** Pre-load existing code for editing */
  initialCode?: string;
  /** Pre-load existing function name */
  initialName?: string;
  /** Pre-load existing function description */
  initialDescription?: string;
  /** Pre-load language */
  initialLanguage?: 'typescript' | 'python';
  /**
   * Functions-expansion (2026-05-12) — helper-chat scope.
   *
   * - 'tenant' (default): tenant chat-driven authoring of a custom
   *   `tenant_functions` row. Snapshot includes the tenant's existing
   *   functions + secrets + connections. Helper context refers to
   *   "your workspace".
   * - 'sysadmin': sysadmin chat-driven authoring of a wrapper-function
   *   row (W3-CELL-07 sandbox). Snapshot scopes to system functions +
   *   model catalog. Helper context refers to "the platform".
   *
   * Backend (studio-helper-chat.ts) already supports both scopes; this
   * prop just forwards to `useHelperChat`. Save callback is supplied
   * by the caller so the same Monaco + chat shell can persist into
   * either store.
   */
  scope?: HelperScope;
  /**
   * Title pill shown next to the ArgusEye in the header. Defaults to
   * "Argus Tool Developer" for tenant scope; sysadmin callers pass a
   * scope-appropriate title (e.g. "Sysadmin Wrapper Function Authoring").
   */
  headerTitle?: string;
  /** Subtitle line in the header. */
  headerSubtitle?: string;
  /**
   * Where the back / cancel buttons navigate. Defaults to
   * '/connections/functions' for tenant scope. Sysadmin callers pass
   * '/sysadmin/catalog/functions'.
   */
  backHref?: string;
  /**
   * Render-prop slot below the Monaco editor footer. Sysadmin callers
   * mount their lifecycle metadata strip (function_key, category,
   * applies_to, latency_budget_ms, version) here.
   *
   * Receives the current code/name/description/language so the caller
   * can wire its own form state from the Accept/Reject events.
   */
  renderFooterExtension?: (args: {
    code: string;
    name: string;
    description: string;
    language: 'typescript' | 'python';
  }) => React.ReactNode;
  /**
   * Label for the primary save button. Defaults to "Save as Draft" for
   * tenant scope; sysadmin uses "Create draft".
   */
  saveLabel?: string;
  /**
   * R2 P1-3 (PR #470 audit fix): switches the welcome message + chips
   * to the right "I'm Argus X Developer" intro. Defaults to 'tool'
   * for back-compat with the existing tools-authoring callers. The
   * functions-authoring route at /connections/new-function passes
   * 'function' so the conversation seed and chips match the entity
   * the user actually said they wanted to build (per memory rule
   * `feedback_ai_conversation.md`).
   */
  entityKind?: 'tool' | 'function';
}

export function ToolDeveloperPage({
  onSave,
  initialCode,
  initialName,
  initialDescription,
  initialLanguage,
  scope = 'tenant',
  headerTitle = 'Argus Tool Developer',
  headerSubtitle = 'AI-assisted tool creation',
  backHref = '/connections/functions',
  renderFooterExtension,
  saveLabel = 'Save as Draft',
  entityKind = 'tool',
}: ToolDeveloperPageProps) {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const editorRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State — if editing existing function, pre-load code and skip discovery
  const isEditing = !!initialCode;
  const [messages, setMessages] = useState<ToolDevMessage[]>(() => {
    if (isEditing) {
      // R2 P1-3: use entityKind to disambiguate "this tool" vs "this function"
      // in the edit-mode greeting so the assistant doesn't say "tool" when
      // the user clicked Edit on a Function row.
      const kindNoun = entityKind === 'function' ? 'function' : 'tool';
      return [
        makeMessage({
          role: 'argus',
          type: 'text',
          content: `I've loaded **${initialName || `your ${kindNoun}`}** into the editor.\n\nI can help you:\n- **Review** the code for security and best practices\n- **Refactor** or improve specific parts\n- **Add features** like caching, rate limiting, or error handling\n- **Generate tests** to verify it works correctly\n\nWhat would you like to do?`,
        }),
        makeMessage({
          role: 'argus',
          type: 'suggestion_chips',
          content: '',
          chips: [
            { id: 'review', label: 'Review this code', emoji: '🔍', action: 'Review this code for security and best practices' },
            { id: 'improve', label: 'Suggest improvements', emoji: '✨', action: 'Suggest improvements to make this code better' },
            { id: 'test', label: 'Generate tests', emoji: '🧪', action: `Generate test cases for this ${kindNoun}` },
            { id: 'explain', label: 'Explain the code', emoji: '💡', action: 'Walk me through the code line by line' },
          ],
        }),
      ];
    }
    return getWelcomeMessages(entityKind).map(m => makeMessage(m));
  });
  const [input, setInput] = useState('');
  const [isArgusTyping, setIsArgusTyping] = useState(false);
  const [phase, setPhase] = useState<ToolDevPhase>(isEditing ? 'refine' : 'discover');
  const [language, setLanguage] = useState<'typescript' | 'python'>(initialLanguage ?? 'typescript');
  const [code, setCode] = useState(initialCode ?? '');
  const [toolName, setToolName] = useState(initialName ?? '');
  const [toolDescription, setToolDescription] = useState(initialDescription ?? '');
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [editorFullscreen, setEditorFullscreen] = useState(false);
  const [cursorPos, setCursorPos] = useState({ line: 1, col: 1 });

  // Full function definition captured from helper mutations. These were
  // previously dropped on the floor (Phase-1 no-op), so saved functions had
  // empty schemas, no egress allowlist, and no side-effects stance — making
  // them un-typed for agents, egress-dead, and unpublishable. Now captured so
  // they round-trip through onSave → backend persistence + refinement turns.
  const [fnInputSchemaJson, setFnInputSchemaJson] = useState('');
  const [fnOutputSchemaJson, setFnOutputSchemaJson] = useState('');
  const [fnSideEffects, setFnSideEffects] = useState<string[]>([]);
  const [fnApproval, setFnApproval] = useState('');
  const [fnNetworkHosts, setFnNetworkHosts] = useState<string[]>([]);
  const [fnTests, setFnTests] = useState<Array<{ name: string; inputJson: string; expectedJson?: string }>>([]);

  // Day 5 §6.4 — live Function Helper chat. Canvas state is the
  // current editor draft + metadata; the helper patches via mutation
  // tool calls (set_code_body, set_metadata, set_input_schema, etc.).
  const helper = useHelperChat({
    kind: 'function_helper',
    scope,
    canvasState: {
      runtime: language,
      name: toolName,
      description: toolDescription,
      code,
      // Include accepted definition so refinement turns don't lose prior state.
      inputSchemaJson: fnInputSchemaJson || undefined,
      outputSchemaJson: fnOutputSchemaJson || undefined,
      sideEffects: fnSideEffects.length ? fnSideEffects : undefined,
      networkHosts: fnNetworkHosts.length ? fnNetworkHosts : undefined,
      tests: fnTests.length ? fnTests : undefined,
    },
  });

  // Mirror helper.messages into the existing rich ToolDevMessage list so
  // the welcome cards + file-upload status messages keep rendering. The
  // helper owns its own turn log; we track the last index we've copied AND
  // map each mirrored helper index → the rich message id we created for it.
  //
  // Bug #10 (connections rail): the helper's tail assistant message GROWS
  // IN PLACE as `type:'token'` SSE frames arrive (use-studio-helper `send`
  // mutates the last message's content). The previous mirror snapshotted
  // each helper message exactly once and immediately advanced the pointer
  // past the tail, so the streaming assistant bubble was copied while still
  // empty and never re-synced — the user saw a perpetual empty Argus bubble
  // even though the backend streamed the full clarifying text. Fix: append
  // NEW helper messages once, but on every helper.messages change re-sync
  // the tail (still-streaming) message's content into its mirrored row.
  const lastCopiedHelperIdxRef = useRef(0);
  const helperIdxToRichIdRef = useRef<Map<number, string>>(new Map());
  useEffect(() => {
    if (helper.messages.length === 0) return;
    const startIdx = lastCopiedHelperIdxRef.current;
    const newRows: ToolDevMessage[] = [];
    for (let i = startIdx; i < helper.messages.length; i++) {
      const m = helper.messages[i]!;
      const row = makeMessage({
        role: m.role === 'user' ? 'user' : 'argus',
        type: 'text',
        content: m.content,
      });
      helperIdxToRichIdRef.current.set(i, row.id);
      newRows.push(row);
    }
    lastCopiedHelperIdxRef.current = helper.messages.length;

    const tailIdx = helper.messages.length - 1;
    const tailContent = helper.messages[tailIdx]!.content;
    const tailRichId = helperIdxToRichIdRef.current.get(tailIdx);
    if (newRows.length === 0 && !tailRichId) return;

    setMessages((prev) => {
      let next = newRows.length > 0 ? [...prev, ...newRows] : prev;
      if (tailRichId) {
        next = next.map((row) =>
          row.id === tailRichId && row.content !== tailContent
            ? { ...row, content: tailContent }
            : row,
        );
      }
      return next;
    });
  }, [helper.messages]);

  // Mirror streaming state to the existing isArgusTyping UI bits.
  useEffect(() => {
    setIsArgusTyping(helper.isStreaming);
  }, [helper.isStreaming]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, helper.isStreaming]);

  /**
   * Apply one function_helper mutation against the editor state.
   * Maps every tool name → setState call.
   */
  const applyMutation = useCallback((m: PendingMutation) => {
    const a = m.args as any;
    switch (m.toolName) {
      case 'set_metadata':
        if (a.name != null) setToolName(String(a.name));
        if (a.description != null) setToolDescription(String(a.description));
        if (a.runtime === 'python' || a.runtime === 'ts') {
          setLanguage(a.runtime === 'ts' ? 'typescript' : 'python');
        }
        if (phase === 'discover' || phase === 'clarify') setPhase('generate');
        break;
      case 'set_code_body':
        setCode(String(a.code ?? ''));
        if (a.language === 'python') setLanguage('python');
        else if (a.language === 'ts') setLanguage('typescript');
        if (phase === 'discover' || phase === 'clarify') setPhase('generate');
        else if (phase === 'generate') setPhase('refine');
        break;
      case 'set_input_schema':
        if (a.schemaJson != null) setFnInputSchemaJson(String(a.schemaJson));
        break;
      case 'set_output_schema':
        if (a.schemaJson != null) setFnOutputSchemaJson(String(a.schemaJson));
        break;
      case 'set_policy':
        if (Array.isArray(a.sideEffects)) setFnSideEffects(a.sideEffects.map(String));
        if (a.approval != null) setFnApproval(String(a.approval));
        break;
      case 'set_network_hosts':
        if (Array.isArray(a.hosts)) setFnNetworkHosts(a.hosts.map(String));
        break;
      case 'add_test_case':
        if (a.name != null && a.inputJson != null) {
          setFnTests(prev => [
            ...prev,
            { name: String(a.name), inputJson: String(a.inputJson), expectedJson: a.expectedJson != null ? String(a.expectedJson) : undefined },
          ]);
        }
        break;
      case 'add_valve':
        // Valves (env vars the function reads via ctx.params) — surfaced in the
        // chat transcript; populated in the function settings page post-deploy.
        break;
    }
    helper.consumeMutation(m.callId);
  // #769 — depend on STABLE refs out of the helper, not the helper
  // object itself. `useHelperChat` returns a fresh wrapper object on
  // every render; capturing it whole would make this callback
  // recompute every render and any downstream effect that depends on
  // it (real or future) would loop with React error #185 (cf. the
  // trust/audit fix 12daa2e6). `helper.consumeMutation` is a stable
  // useCallback from the hook so it's safe to capture by reference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helper.consumeMutation, phase]);

  const applyAll = useCallback(() => {
    for (const m of helper.pendingMutations) {
      if (m.confirmed && m.turnComplete && !m.invalidReason) applyMutation(m);
    }
    // #769 — `helper.pendingMutations` is a useState array reference
    // from the hook; capturing it directly (not the wrapping helper
    // object) keeps applyAll stable when only the wrapper rebuilds.
  }, [helper.pendingMutations, applyMutation]);

  // Send message — replaces generateMockResponse path.
  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || helper.isStreaming || helper.pendingMutations.length > 0) return;
    // Add the user turn into the rich-message log first so attachments
    // render (helper.messages doesn't carry attachments).
    const userMsg = makeMessage({
      role: 'user',
      type: 'text',
      content: text,
      attachments: attachments.length > 0 ? [...attachments] : undefined,
    });
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setAttachments([]);

    // Bump lastCopiedHelperIdxRef past the upcoming user turn the helper
    // will add to its log so we don't double-render the user line.
    lastCopiedHelperIdxRef.current += 1;

    // Phase tracking — preserved for legacy UI bits.
    if (phase === 'discover') setPhase('clarify');

    void helper.send(text);
  // #769 — same stability rule as applyMutation: capture stable refs
  // out of helper (send is a useCallback inside the hook). Listing the
  // wrapping helper object would make handleSend recompute every
  // render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, helper.send, helper.isStreaming, helper.pendingMutations, attachments, phase]);

  // Chip click — same behaviour as Send with the chip action.
  const handleChipClick = useCallback((chip: SuggestionChip) => {
    if (helper.isStreaming || helper.pendingMutations.length > 0) return;
    setInput('');
    const userMsg = makeMessage({ role: 'user', type: 'text', content: chip.action });
    setMessages((prev) => [...prev, userMsg]);
    lastCopiedHelperIdxRef.current += 1;
    if (phase === 'discover') setPhase('clarify');
    void helper.send(chip.action);
  // #769 — stable refs only; see handleSend rationale above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helper.send, helper.isStreaming, helper.pendingMutations, phase]);

  // File upload
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        const attachment: FileAttachment = {
          id: makeId(),
          name: file.name,
          type: file.type || 'text/plain',
          size: file.size,
          content,
        };
        setAttachments(prev => [...prev, attachment]);

        // Auto-process file upload with AI
        const responseMessages = processFileUpload(file.name, content).map(m => makeMessage(m));
        setMessages(prev => [
          ...prev,
          makeMessage({ role: 'user', type: 'file_reference', content: `Uploaded **${file.name}** (${(file.size / 1024).toFixed(1)} KB)`, attachments: [attachment] }),
          ...responseMessages,
        ]);
      };
      reader.readAsText(file);
    });
    e.target.value = '';
  };

  // Keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Save — when no onSave provided, fall back to a generic toast +
  // navigate-back. Sysadmin callers always pass an onSave that calls
  // useCreateWrapperFunction; tenant callers pass an onSave that hits
  // useTenantFunctions.
  //
  // The 4th positional `language` arg lets sysadmin map back to its
  // 'ts'|'py' enum. Tenant callers ignore it.
  const handleSave = () => {
    if (onSave) {
      onSave(code, toolName, toolDescription, language, {
        inputSchemaJson: fnInputSchemaJson || undefined,
        outputSchemaJson: fnOutputSchemaJson || undefined,
        sideEffects: fnSideEffects.length ? fnSideEffects : undefined,
        networkHosts: fnNetworkHosts.length ? fnNetworkHosts : undefined,
        tests: fnTests.length ? fnTests : undefined,
      });
    } else {
      toast.success(`Function "${toolName || 'custom.new_tool'}" saved as draft`);
      navigate({ to: backHref });
    }
  };

  // Find last active chips
  const lastChipsMsg = [...messages].reverse().find(m => m.type === 'suggestion_chips' && m.chips);
  const chipsUsed = lastChipsMsg
    ? messages.some(m => m.role === 'user' && m.timestamp > lastChipsMsg.timestamp)
    : true;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-4 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: backHref })} aria-label={`Back to ${headerTitle}`} title="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex items-center gap-2">
            <ArgusEye state="idle" className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-sm font-semibold">{headerTitle}</h1>
              <p className="text-[10px] text-muted-foreground">{headerSubtitle}</p>
            </div>
          </div>
          {scope === 'sysadmin' && (
            <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-700 dark:text-amber-400">
              sysadmin
            </Badge>
          )}
          <Badge variant="outline" className={cn('text-[9px]', PHASE_LABELS[phase].color)}>
            {PHASE_LABELS[phase].label}
          </Badge>
          {toolName && (
            <Badge variant="secondary" className="text-[9px] font-mono">{toolName}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate({ to: backHref })}>Cancel</Button>
          <Button size="sm" disabled={!code.trim()} onClick={handleSave}>
            <Rocket className="h-3.5 w-3.5 mr-1.5" /> {saveLabel}
          </Button>
        </div>
      </div>

      {/* Split: Chat + Editor */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Conversation */}
        <div className={cn('flex flex-col border-r', editorFullscreen ? 'w-0 overflow-hidden' : 'w-[440px] shrink-0')}>
          {/*
            R2 P1-3: removed the "scripted flow" banner. As of Day 5 §6.4
            the helper chat is wired through `useHelperChat` (real LLM
            backend at /api/v1/studio-helper-chat) — see imports at
            the top of this file. The previous banner described an
            earlier client-side keyword matcher that no longer exists
            and directly violated the founder-locked rule
            "AI must feel like AI / no canned templates"
            (memory feedback_ai_conversation.md, caught by the
            Round-2 audit memo PR #470 P1-3).

            We intentionally do NOT replace the banner with new copy —
            the helper-chat header pill already shows the active phase
            and model context, and adding more chrome above the
            transcript would just shrink the conversation viewport. If
            an explicit "AI-generated content" disclosure is needed
            later, it should attach to individual assistant turns, not
            sit as a global header.
          */}
          {/* Messages */}
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {messages.map(msg => {
              if (msg.type === 'suggestion_chips' && msg.chips) {
                const isActive = msg === lastChipsMsg && !chipsUsed;
                if (!isActive) return null;
                return (
                  <div key={msg.id} className="flex flex-wrap gap-1.5 ml-9 mt-1">
                    {msg.chips.map((chip, i) => (
                      <button
                        key={chip.id}
                        onClick={() => handleChipClick(chip)}
                        disabled={isArgusTyping}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/40 active:scale-95 transition-all disabled:opacity-50"
                        style={{ animationDelay: `${i * 50}ms` }}
                      >
                        {chip.emoji && <span className="text-sm">{chip.emoji}</span>}
                        {chip.label}
                      </button>
                    ))}
                  </div>
                );
              }

              if (msg.role === 'argus') {
                return (
                  <div key={msg.id} className="flex gap-2.5">
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <ArgusEye state="idle" className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 max-w-[88%] space-y-2">
                      <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3">
                        <MarkdownRenderer content={msg.content} />
                      </div>
                      {/* Review items */}
                      {msg.type === 'review_result' && msg.reviewItems && (
                        <div className="space-y-1 ml-1">
                          {msg.reviewItems.map((item, i) => {
                            const { icon: Icon, color } = REVIEW_ICONS[item.severity];
                            return (
                              <div key={i} className="flex items-start gap-2 text-xs">
                                <Icon className={cn('h-3.5 w-3.5 mt-0.5 shrink-0', color)} />
                                <span className="text-muted-foreground">
                                  {item.message}
                                  {item.line && <span className="font-mono text-[10px] ml-1 text-muted-foreground/60">(line {item.line})</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              if (msg.role === 'user') {
                return (
                  <div key={msg.id} className="flex justify-end">
                    <div className="max-w-[85%]">
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="flex gap-1.5 justify-end mb-1">
                          {msg.attachments.map(att => (
                            <Badge key={att.id} variant="secondary" className="text-[9px] gap-1">
                              <FileText className="h-2.5 w-2.5" /> {att.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
                        {msg.content}
                      </div>
                    </div>
                  </div>
                );
              }

              if (msg.role === 'system') {
                return (
                  <div key={msg.id} className="text-center">
                    <span className="text-[10px] text-muted-foreground/60 italic">{msg.content}</span>
                  </div>
                );
              }

              return null;
            })}

            {/* Typing indicator */}
            {isArgusTyping && (
              <div className="flex gap-2.5">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <ArgusEye state="thinking" blink="fast" className="h-5 w-5 text-primary" />
                </div>
                <div className="bg-muted/40 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Attachment bar */}
          {attachments.length > 0 && (
            <div className="px-3 pt-2 flex gap-1.5 flex-wrap border-t border-border/40">
              {attachments.map(att => (
                <Badge key={att.id} variant="secondary" className="text-[9px] gap-1 pr-0.5">
                  <FileCode className="h-2.5 w-2.5" />
                  {att.name}
                  <button
                    onClick={() => setAttachments(prev => prev.filter(a => a.id !== att.id))}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-muted"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Day 5 §3.3 — Accept/Reject stack mounts above the input.
              Input + Send disabled while there are pending mutations. */}
          {helper.pendingMutations.length > 0 && (
            <div className="px-3 pt-2 shrink-0">
              <AcceptRejectCard
                mutations={helper.pendingMutations}
                helperLabel="Function Helper"
                applyMutation={applyMutation}
                rejectMutation={(m) => helper.consumeMutation(m.callId)}
                applyAll={applyAll}
                rejectAll={() => helper.clearMutations()}
              />
            </div>
          )}
          {/* Input area */}
          <div className="p-3 border-t border-border/40 shrink-0">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 shrink-0"
                onClick={() => fileInputRef.current?.click()}
                title="Attach file (.md, .ts, .py, .json)"
                aria-label="Attach file"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".md,.txt,.ts,.tsx,.js,.py,.json,.yaml,.yml,.csv"
                multiple
                onChange={handleFileSelect}
              />
              <Textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  phase === 'discover' ? 'Describe the tool you want to build...' :
                  phase === 'clarify' ? 'Answer the questions or provide more details...' :
                  phase === 'review' || phase === 'refine' ? 'Ask for changes, review, or test...' :
                  phase === 'test' ? 'Run tests or request more test cases...' :
                  phase === 'ready' ? 'Any final changes before saving?' :
                  'Type a message...'
                }
                className="min-h-[40px] max-h-[100px] resize-none text-sm"
                rows={1}
                disabled={isArgusTyping || helper.pendingMutations.length > 0}
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isArgusTyping || helper.pendingMutations.length > 0}
                size="icon"
                className="h-10 w-10 shrink-0 rounded-xl"
                aria-label="Send message"
                title={helper.pendingMutations.length > 0 ? 'Resolve pending suggestions first' : 'Send'}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Right: Code Editor */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Editor toolbar */}
          <div className="px-3 py-1.5 border-b border-border/40 flex items-center justify-between shrink-0 bg-card">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={cn('text-[9px]',
                language === 'typescript' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
              )}>
                {language === 'typescript' ? 'TypeScript' : 'Python'}
              </Badge>
              {code && <span className="text-[10px] text-muted-foreground">{code.split('\n').length} lines</span>}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Format code"
                onClick={() => editorRef.current?.getAction('editor.action.formatDocument')?.run()}>
                <AlignLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" title="Copy code"
                onClick={() => { navigator.clipboard.writeText(code); toast.success('Code copied'); }}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" title={editorFullscreen ? 'Show chat' : 'Fullscreen editor'}
                onClick={() => setEditorFullscreen(!editorFullscreen)}>
                {editorFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>

          {/* Monaco Editor — always mounted so a user can paste code to
              bootstrap a function manually (bug #18 part b). The empty-state
              copy used to promise 'paste code directly' but rendered a
              non-editable placeholder, so the manual path was a dead end. */}
          <div className="flex-1 min-h-0">
            {true ? (
              <Editor
                height="100%"
                language={language === 'typescript' ? 'typescript' : 'python'}
                value={code}
                onChange={v => setCode(v ?? '')}
                theme={mode === 'dark' ? 'vs-dark' : 'light'}
                onMount={(editor, monaco) => {
                  editorRef.current = editor;
                  editor.onDidChangeCursorPosition(e => {
                    setCursorPos({ line: e.position.lineNumber, col: e.position.column });
                  });
                  editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
                    handleSave();
                  });
                  if (language === 'typescript') {
                    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                      noSemanticValidation: true,
                      noSyntaxValidation: false,
                    });
                  }
                }}
                options={{
                  minimap: { enabled: editorFullscreen },
                  fontSize: 13,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  tabSize: language === 'python' ? 4 : 2,
                  padding: { top: 12, bottom: 12 },
                  bracketPairColorization: { enabled: true },
                  guides: { bracketPairs: true, indentation: true },
                  folding: true,
                  renderLineHighlight: 'all',
                  smoothScrolling: true,
                  cursorBlinking: 'smooth',
                  cursorSmoothCaretAnimation: 'on',
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <Sparkles className="h-10 w-10 text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">Code will appear here</p>
                <p className="text-xs text-muted-foreground/70 mt-1 max-w-sm">
                  Describe your tool in the chat and Argus will generate the code for you.
                  Or paste code directly to start editing.
                </p>
              </div>
            )}
          </div>

          {/* Editor footer */}
          {code && (
            <div className="px-3 py-1.5 border-t border-border/40 flex items-center justify-between shrink-0 bg-card">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono text-muted-foreground">Ln {cursorPos.line}, Col {cursorPos.col}</span>
                <span className="text-[10px] text-muted-foreground">Ctrl+S to save</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={() => {
                  toast.info('Running test...');
                  setTimeout(() => toast.success('Test passed — 145ms'), 1500);
                }}>
                  <Play className="h-3 w-3" /> Quick Test
                </Button>
              </div>
            </div>
          )}

          {/* Functions-expansion (2026-05-12) — render-prop slot for
              sysadmin lifecycle metadata (function_key, category,
              applies_to, latency_budget_ms, version). Tenant callers
              omit this prop; sysadmin caller mounts the metadata strip
              from catalog.functions.new.tsx. Sits flush below the
              editor footer in the right column. */}
          {renderFooterExtension && (
            <div className="shrink-0 border-t border-border/40 bg-card">
              {renderFooterExtension({
                code,
                name: toolName,
                description: toolDescription,
                language,
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

